import React, { MouseEventHandler, ReactElement } from 'react';
import { t } from 'i18next';
import ImageGallery, { ReactImageGalleryItem, ReactImageGalleryProps } from 'react-image-gallery';
import styled from 'styled-components';
import { screen } from '../../common/breakpoints';
import { HorizontalSpacing, VerticalSpacing } from '../../common/spacings';
import { ReactComponent as Back } from '../../assets/icons/back-small.svg';
import { ReactComponent as Forward } from '../../assets/icons/forward-small.svg';

const StyledGalleryContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledTextWrapper = styled.div`
  padding-left: ${HorizontalSpacing.S};
  padding-top: ${VerticalSpacing.S};
  padding-bottom: ${VerticalSpacing.S};

  @media screen and ${screen.m} {
    padding-left: ${HorizontalSpacing.M};
    padding-bottom: ${VerticalSpacing.M};
    padding-top: ${VerticalSpacing.M};
  }

  @media screen and ${screen.l} {
    padding-left: ${HorizontalSpacing.L};
    padding-bottom: ${VerticalSpacing.L};
    padding-top: ${VerticalSpacing.L};
  }

  @media screen and ${screen.xl} {
    padding-left: ${HorizontalSpacing.XL};
    padding-bottom: ${VerticalSpacing.XL};
    padding-top: ${VerticalSpacing.XL};
  }
`;

const StyledGalleryHeadline = styled.h2<{ needsSpacing?: boolean }>`
  padding-top: ${(props) => (props.needsSpacing ? `${VerticalSpacing.S}` : '0')};

  @media screen and ${screen.m} {
    padding-top: ${(props) => (props.needsSpacing ? `${VerticalSpacing.M}` : '0')};
  }

  @media screen and ${screen.l} {
    padding-top: ${(props) => (props.needsSpacing ? `${VerticalSpacing.L}` : '0')};
  }

  @media screen and ${screen.xl} {
    padding-top: ${(props) => (props.needsSpacing ? `${VerticalSpacing.XL}` : '0')};
  }
`;

const StyledExteriorContainer = styled.span`
  padding-top: ${VerticalSpacing.S};

  @media screen and ${screen.m} {
    padding-top: ${VerticalSpacing.M};
  }

  @media screen and ${screen.l} {
    padding-top: ${VerticalSpacing.L};
  }

  @media screen and ${screen.xl} {
    padding-top: ${VerticalSpacing.XL};
  }
`;

const StyledImageControls = styled.div``;

const StyledButton = styled.button`
  position: relative;
  background: #333333;
  opacity: 0.7;
  color: white;
  border: 0;
  cursor: pointer;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  z-index: 1;

  &:first-of-type {
    position: absolute;
    padding: 0.5rem;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  &:last-of-type {
    position: absolute;
    padding: 0.5rem;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  &:hover {
    background: #3333337d;
    opacity: 1;
    color: white;
    cursor: pointer;
    text-align: center;
    white-space: nowrap;
  }
`;

const StyledGallery = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 1rem 0 1rem;

  img {
    max-height: fit-content !important;
  }

  svg {
    height: 3.3rem;
    width: 3.3rem;
    transform: translateY(0.1rem);

    @media screen and ${screen.l} {
      height: 2.3rem;
      width: 2.3rem;
    }
  }
`;

export type GalleryProps = {
  assets: string[];
  name: string;
  displayTitle?: boolean;
  titleSpacing?: boolean;
};

const getStaticImages = (assets: string[]) => {
  const images: ReactImageGalleryItem[] = assets.map((asset) => {
    return {
      original: asset,
      thumbnail: asset,
    };
  });

  return images;
};

export const Gallery = (props: GalleryProps): ReactElement<GalleryProps> => {
  const { assets, name, displayTitle, titleSpacing } = props;
  const galleryItems: ReactImageGalleryItem[] = getStaticImages(assets);
  let slideLeft: MouseEventHandler<HTMLElement>;
  let slideRight: MouseEventHandler<HTMLElement>;

  if (galleryItems.length === 0) {
    return <></>;
  }

  const customControls = () => {
    const disabled = !galleryItems || galleryItems.length <= 1;
    return (
      <StyledImageControls>
        <StyledButton
          aria-label="slideLeft"
          onClick={(evt) => {
            slideLeft(evt);
          }}
          disabled={disabled}
        >
          <Back title="" />
        </StyledButton>
        <StyledButton
          aria-label="slideRight"
          onClick={(evt) => {
            slideRight(evt);
          }}
          disabled={disabled}
        >
          <Forward title="" />
        </StyledButton>
      </StyledImageControls>
    );
  };

  const imageProps: ReactImageGalleryProps = {
    showIndex: false,
    showBullets: false,
    infinite: true,
    showFullscreenButton: false,
    showThumbnails: false,
    showPlayButton: false,
    showNav: true,
    isRTL: false,
    slideDuration: 450,
    disableThumbnailScroll: false,
    slideInterval: 2000,
    items: galleryItems,
    renderCustomControls: customControls,
    renderLeftNav: (handler, _disabled) => {
      slideLeft = handler;
      return null;
    },
    renderRightNav: (handler, _disabled) => {
      slideRight = handler;
      return null;
    },
  };

  return (
    <StyledGalleryContainer id="gallery" data-testid="gallery">
      <StyledTextWrapper>
        {displayTitle ? (
          <>
            <span>
              <StyledGalleryHeadline needsSpacing={titleSpacing}>
                {t(`body.details.gallery.title`)}
              </StyledGalleryHeadline>
            </span>
            <p>{t(`body.details.gallery.${name}`)}</p>
          </>
        ) : (
          <StyledExteriorContainer>{t(`body.details.gallery.${name}`)}</StyledExteriorContainer>
        )}
      </StyledTextWrapper>
      <StyledGallery>
        <ImageGallery {...imageProps} />
      </StyledGallery>
    </StyledGalleryContainer>
  );
};
